import { useState, type CSSProperties } from 'react';
import { CHAPTERS, chapterOf } from '../content/chapters';
import { useGameStore } from '../store/gameStore';

/** The campaign map — all seven chapters as a villain path: beaten (⭐) / now (play) /
 *  locked (🔒). Mirrors the band/level progression: a chapter is beaten once he's climbed
 *  past its level. Only the current chapter is launchable (CONTINUE); the rest just show
 *  progress, so the story reads as a clear arc. */
export function StoryMap({ onPlay, onClose }: { onPlay: () => void; onClose: () => void }) {
  const band = useGameStore((s) => s.band);
  const inProgress = useGameStore((s) => s.currentBeatIds.length > 0 && s.beatIndex < s.currentBeatIds.length);
  const curOrder = chapterOf(band)?.order ?? 1;

  return (
    <div style={overlay}>
      <div style={header}>
        <span style={title}>🕸️ Spider-Team vs. the Sinister Six</span>
        <button type="button" style={closeBtn} onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>

      <div style={path}>
        {CHAPTERS.map((c) => {
          const state = c.order < curOrder ? 'beaten' : c.order === curOrder ? 'current' : 'locked';
          return <ChapterNode key={c.id} chapter={c} state={state} inProgress={inProgress} onPlay={onPlay} />;
        })}
      </div>
    </div>
  );
}

function ChapterNode({
  chapter,
  state,
  inProgress,
  onPlay,
}: {
  chapter: (typeof CHAPTERS)[number];
  state: 'beaten' | 'current' | 'locked';
  inProgress: boolean;
  onPlay: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const locked = state === 'locked';
  const art = locked || failed ? (
    <span style={{ fontSize: 44, lineHeight: 1, filter: state === 'beaten' ? 'grayscale(0.4)' : undefined }}>
      {locked ? '🔒' : chapter.villain.emoji}
    </span>
  ) : (
    <img
      src={`${import.meta.env.BASE_URL}villains/${chapter.villain.id}.png`}
      width={56}
      height={56}
      alt=""
      draggable={false}
      onError={() => setFailed(true)}
      style={{ imageRendering: 'pixelated', filter: state === 'beaten' ? 'grayscale(0.5)' : undefined }}
    />
  );

  return (
    <button
      type="button"
      disabled={state !== 'current'}
      onClick={state === 'current' ? onPlay : undefined}
      style={{
        ...node,
        ...(state === 'current' ? nodeCurrent : state === 'beaten' ? nodeBeaten : nodeLocked),
        cursor: state === 'current' ? 'pointer' : 'default',
      }}
    >
      <span style={chBadge}>Ch {chapter.order}</span>
      <span style={artBox}>
        {art}
        {state === 'beaten' && <span style={star}>⭐</span>}
      </span>
      <span style={name}>{chapter.villain.name}</span>
      {state === 'current' && <span style={go}>{inProgress ? 'KEEP READING ▶' : 'PLAY ▶'}</span>}
    </button>
  );
}

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 40,
  background: 'var(--frame)',
  display: 'flex',
  flexDirection: 'column',
  padding: 20,
  overflow: 'auto',
};
const header: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 };
const title: CSSProperties = { fontSize: 24, fontWeight: 800, color: 'var(--gold)' };
const closeBtn: CSSProperties = {
  width: 'var(--target-min)',
  height: 'var(--target-min)',
  borderRadius: '50%',
  border: 'none',
  background: 'var(--frame-2)',
  color: '#f4f4f4',
  fontSize: 24,
  cursor: 'pointer',
};
const path: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexWrap: 'wrap',
  alignContent: 'center',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
};
const node: CSSProperties = {
  width: 124,
  minHeight: 158,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '14px 10px',
  borderRadius: 20,
  borderWidth: 3,
  borderStyle: 'solid',
  borderColor: 'var(--frame-edge)',
  background: 'var(--frame-2)',
  color: '#f4f4f4',
};
const nodeCurrent: CSSProperties = { borderColor: 'var(--gold)', background: 'var(--frame-2)', animation: 'ctc-glow 1.4s ease-in-out infinite' };
const nodeBeaten: CSSProperties = { borderColor: 'var(--good)', opacity: 0.95 };
const nodeLocked: CSSProperties = { opacity: 0.5 };
const chBadge: CSSProperties = { fontSize: 13, fontWeight: 800, color: 'var(--gold)', letterSpacing: 1 };
const artBox: CSSProperties = { position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 58 };
const star: CSSProperties = { position: 'absolute', top: -6, right: -10, fontSize: 22 };
const name: CSSProperties = { fontSize: 16, fontWeight: 800, textAlign: 'center' };
const go: CSSProperties = {
  marginTop: 2,
  fontSize: 13,
  fontWeight: 800,
  color: 'var(--frame)',
  background: 'var(--gold)',
  borderRadius: 999,
  padding: '4px 12px',
};
